import { useEffect, useState, FC, ReactElement } from 'react';
import styled from 'styled-components';
import { $filterNumberDecimal, $BigNumber, $toFixedFormat } from 'common/utils/met';
import { useTranslation } from 'react-i18next';
import { useAddShowVote } from '@/state/base/hooks';
import { useVoteByUser, useMyVoteInfo, useAddVote } from '@/state/vote/hooks';
import { useBalance, useDebounce } from '@/hooks';
import { Button } from '@/components';
import { message } from 'antd';

const Detail: FC = (): ReactElement => {
    const [debounce] = useDebounce(600);
    const [, handAddShowVote] = useAddShowVote();
    const [myVoteInfo, getMyVoteInfo] = useMyVoteInfo();
    const [, { produceListByUser }] = useVoteByUser();
    const [balance, getBalance] = useBalance();
    const [addVote, loadding] = useAddVote();
    const [amount, setAmount] = useState<string | number>('');
    const { t } = useTranslation();

    useEffect(() => {
        getBalance('AMAX', 'amax.token');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const amountKeyUp = (val, maxValue): string => {
        let _list = val.split('.');
        if ((_list.length === 1 && val) || (_list.length === 2 && Number(_list[1]) > 0)) {
            val = Math.min(Number(val), Number(maxValue));
        }
        return String(val);
    };

    const handAddVote = async () => {
        try {
            await addVote(amount);
            handAddShowVote(false);
            message.success(t('add-vote.add vote success'));
            setTimeout(() => {
                getMyVoteInfo();
                produceListByUser();
            }, 2000);
        } catch (e: any) {
            window.toastError(e);
        }
    };
    return (
        <Dialog id='capture'>
            <Mask onClick={() => handAddShowVote(false)}></Mask>
            <Content>
                <Close className="iconfont" onClick={() => handAddShowVote(false)}>
                    &#xe64f;
                </Close>
                <Title>{t('add-vote.add vote')}</Title>
                <NoticeTip>
                    {t('add-vote.current votes')}
                    <span>{myVoteInfo.votes}</span>
                    {t('base.votes')}
                </NoticeTip>
                <Section>
                    <Tip>
                        {t('add-vote.tip')} <span>1 AMAX=10000{t('base.votes')}</span>
                    </Tip>
                    <Div>
                        <Input
                            placeholder="0.0"
                            value={amount}
                            onChange={e => {
                                setAmount($filterNumberDecimal(e.target.value, 4));
                                debounce(() => {
                                    setAmount(amountKeyUp($filterNumberDecimal(e.target.value, 4), $toFixedFormat(balance, 4)));
                                });
                            }}
                        />
                        <Symbol>AMAX</Symbol>
                    </Div>
                    <Balance>
                        {t('add-vote.balance')} {balance} AMAX <span onClick={() => {
                          if (!balance) {
                            return
                          }
                          setAmount($toFixedFormat(balance, 4))
                        }}>{t('add-vote.all')}</span>
                    </Balance>
                </Section>
                <Footer>
                    <Notice>
                        <VoteTitle>{t('add-vote.end votes')}</VoteTitle>
                        <p>
                            {$BigNumber(amount || 0)
                                .multipliedBy(10000)
                                .plus(myVoteInfo.votes)
                                .toFixed(0)}
                            {amount && (
                                <span>
                                    ({myVoteInfo.votes}+{' '}
                                    <b>
                                        {$BigNumber(amount || 0)
                                            .multipliedBy(10000)
                                            .toFixed(0)}
                                    </b>
                                    )
                                </span>
                            )}
                        </p>
                    </Notice>
                    <Submit
                        loading={loadding}
                        disabled={!amount || Number(amount) === 0}
                        onClick={() => {
                            handAddVote();
                        }}
                    >
                        {t('add-vote.submit')}
                    </Submit>
                </Footer>
            </Content>
        </Dialog>
    );
};
export default Detail;

const Dialog = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 999;
`;
const Mask = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
`;

const Content = styled.div`
    position: absolute;
    left: 0;
    width: 100%;
    padding: 0.14rem 0.12rem 0.23rem;
    background: #fff;
    background: #ffffff;
    border-radius: 0.08rem 0.08rem 0 0;

    @media screen and (max-width: 449px) {
        bottom: 0;
    }
    @media screen and (min-width: 450px) {
        top: 50%;
        left: 50%;
        width: 500px;
        transform: translate(-50%, -50%);
    }
`;
const Close = styled.i`
    position: absolute;
    right: 0.18rem;
    top: 0.13rem;
    cursor: pointer;
    font-size: 0.13rem;
    color: #cccccc;
`;
const Title = styled.h3`
    height: 0.22rem;
    font-family: 'PingFang SC';
    font-style: normal;
    font-weight: 600;
    font-size: 0.16rem;
    line-height: 0.22rem;
    text-align: center;
    color: #444444;
`;
const NoticeTip = styled.p`
    margin: 0.24rem 0 0.12rem;
    height: 0.2rem;
    font-family: 'Alibaba PuHuiTi 2.0';
    font-style: normal;
    font-weight: 400;
    font-size: 0.14rem;
    line-height: 0.2rem;
    color: #21212f;
    span {
        font-family: 'D-DIN Exp';
        font-size: 0.18rem;
        margin: 0 0.03rem;
    }
`;
const Section = styled.section`
    background: #fafafc;
    border-radius: 0.08rem;
    padding: 0.12rem 0.12rem 0.09rem;
`;
const Tip = styled.div`
    height: 0.2rem;
    font-family: 'PingFang SC';
    font-style: normal;
    font-weight: 400;
    font-size: 0.14rem;
    color: #21212f;
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
        font-weight: 400;
        font-size: 0.12rem;
        color: #000000;
        opacity: 0.3;
    }
`;
const Div = styled.div`
    margin: 0.06rem 0 0.1rem;
    height: 0.66rem;
    border-bottom: 0.01rem solid#EEEEEE;
    display: flex;
    align-items: center;
`;
const Input = styled.input`
    flex: 1;
    height: 100%;
    background: none;
    border: none;
    outline: none;
    font-family: 'D-DIN Exp';
    font-style: normal;
    font-weight: 700;
    font-size: 0.2rem;
    color: #3555ff;
    &::placeholder {
        text-indent: 0;
        font-size: 0.2rem;
    }
`;
const Symbol = styled.div`
    font-family: 'PingFang SC';
    font-style: normal;
    font-weight: 600;
    font-size: 0.16rem;
    color: #000000;
`;
const Balance = styled.p`
    height: 0.17rem;
    font-family: 'PingFang SC';
    font-style: normal;
    font-weight: 400;
    font-size: 0.12rem;
    line-height: 0.17rem;
    color: rgba(33, 33, 47, 0.3);
    span {
        cursor: pointer;
        color: #3555ff;
        margin-left: 0.1rem;
    }
`;
const Footer = styled.footer`
    border-radius: 0.08rem;
    margin-top: 0.37rem;
    display: flex;
    justify-content: space-between;
    align-content: space-between;
`;

const Notice = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    justify-items: center;
    p {
        font-family: 'D-DIN Exp';
        font-style: normal;
        font-weight: 700;
        font-size: 0.2rem;
        line-height: 0.2rem;
        color: #3555ff;
        span {
            height: 0.13rem;
            font-family: 'D-DIN Exp';
            font-style: normal;
            font-weight: 400;
            font-size: 0.12rem;
            line-height: 0.13rem;
            color: #21212f;
            margin-left: 0.05rem;
            b {
                color: #3555ff;
                font-weight: 400;
            }
        }
    }
`;
const VoteTitle = styled.div`
    font-family: 'Alibaba PuHuiTi 2.0';
    font-style: normal;
    font-weight: 400;
    font-size: 0.14rem;
    line-height: 0.17rem;
    color: #000000;
    opacity: 0.5;
    margin-bottom: 0.06rem;
`;
const Submit = styled(Button)`
    width: auto;
    min-width: 40%;
    padding: 0 0.3rem;
    height: 0.6rem;
    background: #21212f;
    border-radius: 0.12rem;
    font-family: 'PingFang SC';
    font-style: normal;
    font-weight: 600;
    font-size: 0.18rem;
    color: #ffffff;
    margin-top: 0.03rem;
`;
